import React, { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { usePersistedSearchkitVariables } from '../common/components/filtering/filters';
import { CONSTANTS_ID } from '../common/constants/constants';
import FormModal from '../common/form/FormModal';
import { useI18n } from '../common/i18n/I18n';
import { searchClient } from '../common/list/slimQuery';
import { areAllFiltersInList } from '../common/utils/commonUtils';
import { extractNames } from '../production/helpers/peopleUtils';
import GuestEditor from './GuestEditor';
import Validator from '../common/form/Validator';
import { FormProvider } from '../common/form/FormContext';
import { cloneDeep } from 'lodash';
import { EMPTY_STATE } from './person';
import useHistory from '../common/utils/useHistory';
import { Reference } from 'sr-types/lib/person/v1/graphql';
import { ContactInfo } from 'sr-types/lib/search/v1/graphql';

const validationRules = {
    'name.firstName': [Validator.RULES.isRequired],
    'name.lastName': [Validator.RULES.isRequired],
    'contactInfo.email.address': [Validator.RULES.email]
};

export default ({
    onClose,
    isOpen,
    setIsOpen,
    orgRole,
    id,
    onAddToShortlist,
    shortlistLineItems
}: {
    onClose?: () => void;
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    orgRole?: string;
    id: string;
    onAddToShortlist?: (data: { name: string; reference: Reference; id: string, contactInfo: ContactInfo }) => void;
    shortlistLineItems?: RefObject<any[]>;
}) => {
    const [saveDisabled, setIsSaveDisabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const guestEditorRef = useRef(null);
    const { variables } = usePersistedSearchkitVariables(id);
    const { routeParams } = useHistory();
    const { entityId: entId } = routeParams;

    const entityId = useMemo(() => {
        return CONSTANTS_ID.createId || entId;
    }, [entId, CONSTANTS_ID.createId]);

    const [values, setValues] = useState<any>({
        ...cloneDeep(EMPTY_STATE),
        name: extractNames(variables.query) || {
            firstName: '',
            lastName: ''
        },
        organizationRole: orgRole || '',
        contactExists: false,
        entityId: entityId,
        isUpdate: false,
        isOrganizationLoading: false,
        isProductionLoading: false
    });

    const saveLineItemToShortlist = () => {
        setIsSaving(true);
        guestEditorRef.current.handleSave(
            (input, id) => {
                searchClient.refetchQueries({
                    include: ['slimResultsQuery'],
                    onQueryUpdated(observableQuery) {
                        return areAllFiltersInList(observableQuery.options.variables.filters, [
                            {
                                identifier: 'entity',
                                value: 'Person'
                            }
                        ]);
                    }
                });

                onAddToShortlist({
                    name: `${input.name.firstName} ${input.name.lastName}`,
                    reference: { id: id, label: `${input.name.firstName} ${input.name.lastName}`, type: 'Person' },
                    id: id,
                    contactInfo: input.contactInfo,
                });
                shortlistLineItems.current.push(id);
                setIsSaving(false);
                setIsOpen(false);
            },
            () => {
                setIsSaving(false);
            }
        );
    };

    return (
        isOpen && (
            <FormProvider
                validationRules={validationRules}
                state={values}
                setState={setValues}
                expandedSections={['guest-contact-info']}
            >
                <FormModal
                    id={'shortlist-guest-editor'}
                    title={useI18n('form.shortlist.guestEditor')}
                    isSaving={isSaving}
                    isOpen={isOpen}
                    isSaveDisabled={saveDisabled}
                    onClose={onClose}
                    onSave={saveLineItemToShortlist}
                >
                    <GuestEditor
                        isInModal={true}
                        entityIdFromModal={CONSTANTS_ID.createId}
                        setIsSaveDisabled={setIsSaveDisabled}
                        ref={guestEditorRef}
                        hiddenFields={{
                            creativeCredits: { department: true, showReference: true },
                            productionRole: true,
                            experiences: { deparment: true, title: true, organizationReference: true }
                        }}
                        disabledFields={{
                            organizationRole: true
                        }}
                    />
                </FormModal>
            </FormProvider>
        )
    );
};
